import React, { useCallback, useEffect, useState } from 'react';
import {
  Box, Typography, Paper, Table, TableHead, TableRow, TableCell, TableBody,
  TableContainer, Button, Chip, CircularProgress, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField,
} from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import LockOpenIcon from '@mui/icons-material/LockOpen';
import { getRecentLocks, lockMonth, unlockMonth } from '../../api/timesheetApi';
import { usePermissions } from '../PermissionsContext';
import { handleApiError, handleApiSuccess } from '../Common/ApiErrorHandler';

const MONTH_NAMES = ['', 'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'];

const fmtDateTime = (iso) => {
  if (!iso) return '—';
  const d = new Date(iso);
  if (isNaN(d.getTime())) return '—';
  return d.toLocaleString('sv-SE').slice(0, 16);
};

export default function MonthLockManagementPage() {
  const { hasPermission, loading: permLoading } = usePermissions();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [dialog, setDialog] = useState(null); // { row, mode: 'lock'|'unlock' }
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const allowed = hasPermission?.('Timesheet.ManageMonthLocks');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const data = await getRecentLocks();
      setRows(Array.isArray(data) ? data : []);
    } catch (e) {
      const msg = e?.response?.data || e.message;
      setError(typeof msg === 'string' ? msg : 'Failed to load month locks.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (allowed) load(); }, [allowed, load]);

  const openDialog = (row) => {
    setReason('');
    setDialog({ row, mode: row.isLocked ? 'unlock' : 'lock' });
  };
  const closeDialog = () => { setDialog(null); setReason(''); };

  const submit = async () => {
    if (!dialog || !reason.trim()) return;
    const { row, mode } = dialog;
    setSubmitting(true);
    try {
      if (mode === 'lock') {
        await lockMonth(row.year, row.month, reason.trim());
        handleApiSuccess(`Locked ${row.year}-${String(row.month).padStart(2, '0')}`);
      } else {
        await unlockMonth(row.year, row.month, reason.trim());
        handleApiSuccess(`Unlocked ${row.year}-${String(row.month).padStart(2, '0')}`);
      }
      closeDialog();
      await load();
    } catch (e) {
      handleApiError(e, 'Action failed');
    } finally {
      setSubmitting(false);
    }
  };

  if (!permLoading && !allowed) {
    return (
      <Box sx={{ p: 3 }}>
        <Typography color="text.secondary">You don't have access to Month Locks.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }} data-testid="month-lock-page">
      <Typography variant="h5" sx={{ mb: 1 }}>Month Locks</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Last 12 months of the timesheet month-close lock. The scheduled job auto-locks any month
        that ended more than 7 days ago; you can also force-lock early or unlock with a reason.
      </Typography>

      {error && <Typography color="error" sx={{ mb: 2 }}>{error}</Typography>}

      <Paper sx={{ p: 2 }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}><CircularProgress /></Box>
        ) : (
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Month</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Locked at</TableCell>
                  <TableCell>Locked by</TableCell>
                  <TableCell>Unlocked at / reason</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={`${r.year}-${r.month}`} data-testid={`mlm-row-${r.year}-${r.month}`} hover>
                    <TableCell>{MONTH_NAMES[r.month]} {r.year}</TableCell>
                    <TableCell>
                      {r.isLocked
                        ? <Chip size="small" color="warning" icon={<LockIcon />} label="Locked" data-testid={`mlm-status-${r.year}-${r.month}`} />
                        : <Chip size="small" color="success" icon={<LockOpenIcon />} label="Open" data-testid={`mlm-status-${r.year}-${r.month}`} />}
                    </TableCell>
                    <TableCell>{fmtDateTime(r.lockedAt)}</TableCell>
                    <TableCell>{r.isLocked ? (r.lockedByDisplayName || '—') : '—'}</TableCell>
                    <TableCell>
                      {r.unlockedAt
                        ? <span>{fmtDateTime(r.unlockedAt)}{r.unlockReason ? ` — ${r.unlockReason}` : ''}{r.unlockedByDisplayName ? ` (${r.unlockedByDisplayName})` : ''}</span>
                        : '—'}
                    </TableCell>
                    <TableCell align="center">
                      <Button
                        size="small"
                        variant="outlined"
                        color={r.isLocked ? 'error' : 'primary'}
                        startIcon={r.isLocked ? <LockOpenIcon /> : <LockIcon />}
                        onClick={() => openDialog(r)}
                        data-testid={`mlm-action-${r.year}-${r.month}`}
                      >
                        {r.isLocked ? 'Unlock' : 'Lock'}
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Dialog open={!!dialog} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>
          {dialog?.mode === 'unlock' ? 'Unlock' : 'Lock'} {dialog ? `${MONTH_NAMES[dialog.row.month]} ${dialog.row.year}` : ''}
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            A reason is required and recorded in the audit trail.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            multiline
            minRows={2}
            label="Reason"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            inputProps={{ 'data-testid': 'mlm-reason' }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} data-testid="mlm-cancel">Cancel</Button>
          <Button
            variant="contained"
            color={dialog?.mode === 'unlock' ? 'error' : 'primary'}
            disabled={!reason.trim() || submitting}
            onClick={submit}
            data-testid="mlm-confirm"
          >
            {dialog?.mode === 'unlock' ? 'Unlock' : 'Lock'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
